import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { Workspace } from 'shared'
import { Alert, Button, Card, PageHeader } from '../../components/ui'
import WorkspaceProfileForm, {
  EMPTY_PROFILE,
  toApiPayload,
  type WorkspaceProfileValues,
} from '../../components/workspace/WorkspaceProfileForm'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

export default function PartnerWorkspaceNew() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [values, setValues] = useState<WorkspaceProfileValues>(EMPTY_PROFILE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!values.name.trim()) return

    setSaving(true)
    setError(null)

    try {
      const response = await api.post<{ data: Workspace; kickoff_failed?: boolean }>(
        '/api/partner/workspaces',
        toApiPayload(values)
      )
      navigate(`/partner/${response.data.id}`, {
        state: response.kickoff_failed ? { kickoffFailed: true } : undefined,
      })
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
      setSaving(false)
    }
  }

  return (
    <>
      <PageHeader title={t('partner.createTitle')} subtitle={t('partner.createSubtitle')} />

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <WorkspaceProfileForm values={values} onChange={setValues} disabled={saving} />

          <p className="text-sm text-gray-500 dark:text-gray-400">{t('partner.kickoffNote')}</p>

          {error && <Alert>{error}</Alert>}

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-800">
            <Button type="button" variant="secondary" onClick={() => navigate('/partner')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!values.name.trim()}>
              {t('partner.create')}
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}
